/**
 * Lead collection boundary.
 *
 * API contract: POST /api/leads, multipart/form-data, no browser-held secret.
 * Successful responses MUST be JSON { status: 'accepted', leadId: string }.
 * A Vite SPA fallback returning 200 HTML must never count as a saved lead.
 *
 * Connect the production API through VITE_LEADS_API_URL, or proxy /api/leads.
 * Do not log PII, store drafts, or fall back to mailto without consent.
 */
export type LeadMode = 'quick' | 'detailed'
export type LeadContact = 'call' | 'whatsapp' | 'telegram'
export type ProjectLead = {
  phone: string
  mode: LeadMode
  consent: boolean
  source: string
  idempotencyKey: string
  honeypot: string
  name: string
  business: string
  service: string
  description: string
  budget: string
  timeline: string
  preferredContact: LeadContact
  referenceUrl: string
  attachment: File | null
}

export class LeadApiError extends Error {
  constructor(public readonly code: 'not-configured' | 'rejected' | 'network') {
    super(code)
    this.name = 'LeadApiError'
  }
}

const englishDigits = (value: string) => value
  .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
  .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))

export function normalizeIranianMobile(input: string): string | null {
  const str = englishDigits(input).replace(/[\s\-()]/g, '')
  const local = str.startsWith('+98') ? '0' + str.slice(3) : str.startsWith('0098') ? '0' + str.slice(4) : str.startsWith('98') ? '0' + str.slice(2) : str
  return /^09\d{9}$/.test(local) ? local : null
}

export async function sendLead(lead: ProjectLead): Promise<string> {
  if (!normalizeIranianMobile(lead.phone) || !lead.consent) throw new LeadApiError('rejected')
  const endpoint = (import.meta.env.VITE_LEADS_API_URL as string | undefined)?.trim() || '/api/leads'
  const payload = new FormData()
  payload.set('phone', normalizeIranianMobile(lead.phone)!)
  payload.set('mode', lead.mode)
  payload.set('consent', 'true')
  payload.set('source', lead.source.slice(0,180))
  payload.set('idempotencyKey', lead.idempotencyKey)
  payload.set('website', lead.honeypot)
  payload.set('name', lead.name.trim())
  payload.set('business', lead.business.trim())
  payload.set('service', lead.service)
  payload.set('description', lead.description.trim())
  payload.set('budget', lead.budget)
  payload.set('timeline', lead.timeline)
  payload.set('preferredContact', lead.preferredContact)
  payload.set('referenceUrl', lead.referenceUrl.trim())
  if (lead.attachment) payload.set('attachment', lead.attachment, lead.attachment.name)

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 15000)
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      body: payload,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    })
    if (response.status === 404 || response.status === 501 || response.status === 503) throw new LeadApiError('not-configured')
    if (!response.ok) throw new LeadApiError('rejected')
    if (!response.headers.get('content-type')?.includes('application/json')) throw new LeadApiError('not-configured')
    const body: unknown = await response.json()
    if (!body || typeof body !== 'object' || !('status' in body) || body.status !== 'accepted' ||
      !('leadId' in body) || typeof body.leadId !== 'string' || !body.leadId) {
      throw new LeadApiError('rejected')
    }
    return body.leadId
  } catch (error) {
    if (error instanceof LeadApiError) throw error
    if (error instanceof DOMException && error.name === 'AbortError') throw new LeadApiError('network')
    throw new LeadApiError('network')
  } finally {
    window.clearTimeout(timeout)
  }
}
